import { WORKSPACE_TABS, tabCommandId } from '@ai-studio/shared';

import type { CommandSpec, KeymapProfile } from './registry.js';

/**
 * The shell's own commands, described without behaviour. The project frame
 * registers them with handlers (Workspace); the tab host registers just this
 * table so it can resolve the same key bindings for the native menus.
 */

const TAB_ICONS: Record<string, string> = {
  scene: 'scene',
  model: 'model',
  pixel: 'pixel',
  sound: 'audio',
  video: 'camera',
  play: 'playOutline',
  code: 'code',
  board: 'board',
};

export const CORE_COMMANDS: readonly CommandSpec[] = [
  ...WORKSPACE_TABS.map<CommandSpec>((tab) => ({
    id: tabCommandId(tab.id),
    label: `Go to ${tab.label}`,
    category: 'Window',
    keys: [`Ctrl+${tab.key}`],
    icon: TAB_ICONS[tab.id],
    whileTyping: true,
    keywords: `tab view ${tab.id}`,
  })),
  { id: 'view.settings', label: 'Settings', category: 'Window', keys: ['Ctrl+,'], icon: 'settings', whileTyping: true, keywords: 'preferences providers' },
  { id: 'view.shortcuts', label: 'Keyboard Shortcuts', category: 'Window', keys: ['Ctrl+Alt+K'], icon: 'command', whileTyping: true, keywords: 'keymap hotkeys bindings shortcut manager' },
  { id: 'view.history', label: 'Undo History', category: 'Window', keys: ['Ctrl+Alt+Z'], icon: 'undo', keywords: 'history steps' },
  { id: 'view.build', label: 'Build Settings…', category: 'Window', keys: ['Ctrl+Shift+B'], icon: 'layers', keywords: 'build profiles export player server publish' },
  { id: 'view.particles', label: 'Particle Editor', category: 'Window', icon: 'particles', keywords: 'particle system effect vfx shuriken emitter preview' },
  { id: 'view.helper-bar', label: 'Toggle Shortcut Helper Bar', category: 'Window', icon: 'command', keywords: 'status bar hints keys' },
  { id: 'view.ai', label: 'Toggle AI Panel', category: 'Window', keys: ['Ctrl+J'], icon: 'sparkles', whileTyping: true, keywords: 'assistant chat dock' },
  { id: 'view.explorer', label: 'Code: Explorer', category: 'Window', icon: 'explorer' },
  { id: 'view.git', label: 'Code: Source Control', category: 'Window', icon: 'git' },
  { id: 'view.search', label: 'Code: Search Files', category: 'Window', icon: 'search', hidden: true },
  { id: 'palette.open', label: 'Command Palette', category: 'Search', keys: ['Ctrl+P', 'Ctrl+K'], icon: 'command', whileTyping: true, keywords: 'quick open run' },
  { id: 'search.open', label: 'Search Everything', category: 'Search', keys: ['F3'], icon: 'search', keywords: 'find operator menu search assets entities components' },
  { id: 'favorites.open', label: 'Quick Favorites', category: 'Search', keys: ['Q', 'Shift+Q'], icon: 'sparkles', keywords: 'favourites starred' },
  { id: 'pie.tabs', label: 'Pie: Workspace Tabs', category: 'Pie Menus', keys: ['`'], icon: 'layers' },
  { id: 'pie.window', label: 'Pie: Window & Layout', category: 'Pie Menus', keys: ['Shift+`'], icon: 'panelRight' },
  { id: 'pie.workspaces', label: 'Pie: Workspaces', category: 'Pie Menus', keys: ['Alt+`'], icon: 'layers', keywords: 'layout preset modeling sculpting uv rigging animation lighting scripting' },
  { id: 'layout.maximize', label: 'Maximize Panel', category: 'Layout', keys: ['Ctrl+Space'], icon: 'fullscreen', keywords: 'zoom area fullscreen toggle' },
  { id: 'layout.reset', label: 'Reset Layout', category: 'Layout', icon: 'refresh', keywords: 'factory default revert' },
  { id: 'layout.save', label: 'Save Layout…', category: 'Layout', icon: 'layers' },
  { id: 'edit.undo', label: 'Undo', category: 'Edit', keys: ['Ctrl+Z'], icon: 'undo', repeat: true },
  { id: 'edit.redo', label: 'Redo', category: 'Edit', keys: ['Ctrl+Shift+Z', 'Ctrl+Y'], icon: 'redo', repeat: true },
  { id: 'edit.repeat-last', label: 'Repeat Last', category: 'Edit', keys: ['Shift+R'], icon: 'refresh', keywords: 'again redo last command' },
  { id: 'edit.repeat-history', label: 'Repeat History…', category: 'Edit', keys: ['Ctrl+Alt+R'], icon: 'refresh', keywords: 'recent commands' },
  { id: 'file.save-all', label: 'Save All Documents', category: 'File', keys: ['Ctrl+S'], icon: 'check', keywords: 'flush write' },
  { id: 'file.save', label: 'Save Current File', category: 'File', icon: 'check' },
  { id: 'file.close-project', label: 'Close Project', category: 'File', icon: 'x' },
  { id: 'play.toggle', label: 'Play / Stop', category: 'Play', keys: ['F5'], icon: 'play', whileTyping: true, keywords: 'run game start' },
  { id: 'play.stop', label: 'Stop', category: 'Play', keys: ['Shift+F5'], icon: 'stop', whileTyping: true },
  { id: 'play.pause', label: 'Pause / Resume', category: 'Play', keys: ['F6'], icon: 'pause', whileTyping: true },
  { id: 'engine.reload', label: 'Reload Engine Runtime', category: 'Engine', icon: 'restart' },
];

/** Built-in keymap profiles: only what differs from the command defaults. */
export const BUILTIN_PROFILES: readonly KeymapProfile[] = [
  {
    id: 'default',
    name: 'Default',
    builtin: true,
    description: 'The editor’s own keymap: Ctrl+1–8 tabs, Ctrl+P palette, F3 search, Q favorites.',
    bindings: {},
  },
  {
    id: 'blender',
    name: 'Blender-style',
    builtin: true,
    description: 'F3 operator search, Q Quick Favorites, Ctrl+Space maximize, ` pie, Ctrl+Alt+U preferences, Ctrl+PageUp/Down workspaces.',
    bindings: {
      'palette.open': ['Ctrl+P'],
      'search.open': ['F3'],
      'favorites.open': ['Q'],
      'view.settings': ['Ctrl+Alt+U', 'Ctrl+,'],
      'layout.maximize': ['Ctrl+Space'],
      'view.history': ['Ctrl+Alt+Z'],
      'play.toggle': ['Alt+A', 'F5'],
    },
  },
  {
    id: 'unity',
    name: 'Unity-style',
    builtin: true,
    description: 'Ctrl+P play, Ctrl+Shift+P pause, Ctrl+K search, Shift+Space maximize, Unity window order on Ctrl+1–8.',
    bindings: {
      'play.toggle': ['Ctrl+P', 'F5'],
      'play.pause': ['Ctrl+Shift+P', 'F6'],
      'palette.open': ['Ctrl+Shift+K'],
      'search.open': ['Ctrl+K', 'F3'],
      'layout.maximize': ['Shift+Space'],
      'favorites.open': ['Shift+Q'],
      // Unity: Scene Ctrl+1, Game Ctrl+2, then the rest.
      'view.tab.scene': ['Ctrl+1'],
      'view.tab.play': ['Ctrl+2'],
      'view.tab.model': ['Ctrl+3'],
      'view.tab.pixel': ['Ctrl+4'],
      'view.tab.sound': ['Ctrl+5'],
      'view.tab.code': ['Ctrl+6'],
      'view.tab.board': ['Ctrl+7'],
    },
  },
];
